import { LeafTypes, NodeType } from 'shared/types/ingestion'
import { ILeaf, INode } from 'shared/types/tree'

export function isLeafType(type: unknown): type is LeafTypes {
  return (
    typeof type === 'string' && Object.values<string>(LeafTypes).includes(type)
  )
}

export function isNodeType(type: unknown): type is NodeType {
  return (
    typeof type === 'string' && Object.values<string>(NodeType).includes(type)
  )
}

function hasTreeIds(obj: object) {
  return 'tree_id' in obj && 'version' in obj && 'id' in obj
}

export function isLeaf(item: unknown): item is ILeaf {
  if (!item || typeof item !== 'object') return false
  if ('type' in item) {
    return hasTreeIds(item) && isLeafType(item.type)
  }
  return false
}

export function isNode(item: unknown): item is INode {
  if (!item || typeof item !== 'object') return false

  if (!('type' in item) || !hasTreeIds(item)) return false

  if (!isNodeType(item.type)) return false

  if (isLeaf(item)) {
    return 'nodes' in item
  }
  return true
}

export function breadthFirstTraversal<T, K extends keyof T>(
  object: T,
  childKey: K,
  objFunc: (o: T, parent?: T) => void,
) {
  const objToCheck: [T, T | undefined][] = [[object, undefined]]
  while (objToCheck.length) {
    const [current, parent] = objToCheck.shift()!
    if (!current) continue
    const children = current[childKey]
    if (Array.isArray(children)) {
      objToCheck.push(...children.map((n) => [n, current] as [T, T]))
    }
    objFunc(current, parent)
  }
}

export function breadthFirstNodeCheck(
  tree: INode,
  nodeCheck: (n: INode, parent?: INode) => void,
) {
  breadthFirstTraversal(tree, 'nodes', nodeCheck)
}

export function leavesOfType(node: INode, leafType: LeafTypes) {
  return node.leaves?.filter((l) => l.type === leafType) || []
}

export function leafOfType(node: INode, leafType: LeafTypes) {
  return leavesOfType(node, leafType)[0]
}

export function getSubjectId(tree: INode): number | undefined {
  return leafOfType(tree, LeafTypes.Subject)?.body?.subjectID
}

export function getGradeId(tree: INode): number | undefined {
  return leafOfType(tree, LeafTypes.Grade)?.body?.gradeID
}

// Helper function to ensure all nodes properties are arrays
export function normalizeNodes(node: INode) {
  breadthFirstNodeCheck(node, (n) => {
    if (!n.nodes) n.nodes = []
    n.nodes = n.nodes.reduce<INode[]>((acc, n) => {
      if (n.tree_id === node.tree_id) acc.push(n)
      return acc
    }, [])
  })
  return node
}
